export type SeriesOwnershipType = "internal" | "external";

type SeriesOwnershipCandidate = {
  ownershipType?: string | null;
};

type SeriesRouteCandidate = SeriesOwnershipCandidate & {
  slug?: { current?: string | null } | null;
};

const EXTERNAL_PREFIX = "external-";

export function normalizeSeriesOwnershipType(ownershipType?: string | null): SeriesOwnershipType {
  return ownershipType === "external" ? "external" : "internal";
}

export function selectDefaultSeries<T extends SeriesOwnershipCandidate>(
  series: ReadonlyArray<T | null | undefined> | null | undefined,
): T | null {
  const candidates = series?.filter((candidate): candidate is T => Boolean(candidate)) ?? [];
  return (
    candidates.find((candidate) => normalizeSeriesOwnershipType(candidate.ownershipType) === "internal") ??
    candidates[0] ??
    null
  );
}

export function selectSeriesForRoute<T extends SeriesRouteCandidate>(
  series: ReadonlyArray<T | null | undefined> | null | undefined,
  seriesSlug: string,
  ownershipType?: string | null,
): T | null {
  const slugMatches = series?.filter((candidate): candidate is T => candidate?.slug?.current === seriesSlug) ?? [];
  const requestedOwnershipType = normalizeSeriesOwnershipType(ownershipType);

  return (
    slugMatches.find((candidate) => normalizeSeriesOwnershipType(candidate.ownershipType) === requestedOwnershipType) ??
    selectDefaultSeries(slugMatches)
  );
}

export function parseSeriesRouteSlug(
  slug: string | null | undefined,
): { ownershipType: SeriesOwnershipType; seriesSlug: string } | null {
  if (!slug) {
    return null;
  }

  if (slug.startsWith(EXTERNAL_PREFIX)) {
    const seriesSlug = slug.slice(EXTERNAL_PREFIX.length);
    if (!seriesSlug) {
      return null;
    }

    return {
      ownershipType: "external",
      seriesSlug,
    };
  }

  return {
    ownershipType: "internal",
    seriesSlug: slug,
  };
}

export function getSeriesRouteSlug(input: { ownershipType?: string | null; seriesSlug: string }): string {
  const ownershipType = normalizeSeriesOwnershipType(input.ownershipType);
  return ownershipType === "external" ? `${EXTERNAL_PREFIX}${input.seriesSlug}` : input.seriesSlug;
}

export function getSeriesPath(input: { ownershipType?: string | null; seriesSlug: string }): string {
  return `/series/${getSeriesRouteSlug(input)}`;
}

export function getEpisodePath(input: {
  ownershipType?: string | null;
  seriesSlug: string;
  episodeSlug: string;
}): string {
  return `${getSeriesPath(input)}/${input.episodeSlug}`;
}

export function getEpisodeMeditationPath(input: {
  ownershipType?: string | null;
  seriesSlug: string;
  episodeSlug: string;
}): string {
  return `${getEpisodePath(input)}/meditation`;
}

export function getEpisodePathFromRouteSlug(seriesRouteSlug: string, episodeSlug: string): string {
  return `/series/${seriesRouteSlug}/${episodeSlug}`;
}
